import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen, Star, Trophy, Video, Bell, Calendar,
  LogOut, Download, Clock,
  Award, TrendingUp, Users, Sparkles,
  LayoutDashboard, ClipboardList, FileSpreadsheet,
  Rocket, Camera, CreditCard, CheckCircle2, AlertCircle,
  Plus, Pencil, Trash2, Lock
} from 'lucide-react';
import { useAuth, api } from '../context/AuthContext';
import { toast } from 'sonner';
import ContentViewer from './parent/ContentViewer';
import QuizTaker from './parent/QuizTaker';
import BadgeGallery from './parent/BadgeGallery';
import ParentChatBubble from './parent/ParentChatBubble';
import AvatarPicker from './AvatarPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const CARD_BASE = "bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50";

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptions, setSubscriptions] = useState([]); // [{child_id, is_active, expires_at, ...}]
  const [childDialog, setChildDialog] = useState({ open: false, child: null }); // child=null => add, child=obj => edit
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchDashboard(); fetchSubscriptions(); }, []);

  // Handle post-payment redirect (success_url contains ?payment_session=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('payment_session');
    const cancelled = params.get('payment_cancelled');
    if (cancelled) {
      toast.error('Payment cancelled');
      navigate('/dashboard', { replace: true });
      return;
    }
    if (!sessionId) return;
    // Poll payment status
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/api/subscriptions/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          toast.success('Payment successful — subscription activated! 🎉');
          fetchSubscriptions();
          navigate('/dashboard', { replace: true });
          return;
        }
        if (data.status === 'expired' || attempts >= maxAttempts) {
          toast.error('Payment could not be verified. Please contact us.');
          navigate('/dashboard', { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          toast.error('Payment status check failed');
          navigate('/dashboard', { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    toast.info('Verifying your payment...');
    poll();
  }, [location.search, navigate]);

  const fetchDashboard = async () => {
    try {
      const { data } = await api.get('/api/dashboard/parent');
      setDashboardData(data);
      if (data.children?.length > 0) setSelectedChild(data.children[0]);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired. Please log in again.');
        try { await logout(); } catch (_e) { /* ignore */ }
        navigate('/');
        return;
      }
      toast.error(err?.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  };

  const fetchSubscriptions = async () => {
    try {
      const { data } = await api.get('/api/subscriptions/my');
      setSubscriptions(data);
    } catch { /* ignore */ }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const downloadReport = async (childId, format = 'pdf') => {
    try {
      const token = localStorage.getItem('craven_access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/child/${childId}/${format}`,
        { credentials: 'include', headers }
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedChild?.name || 'child'}_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    } catch { toast.error('Failed to download report'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'family', label: 'Family', icon: Users },
    { id: 'content', label: 'Lessons', icon: BookOpen },
    { id: 'quizzes', label: 'Quizzes', icon: ClipboardList },
    { id: 'badges', label: 'Badges', icon: Trophy },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 border-4 border-[#38BDF8] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const avgScore = selectedChild?.recent_quizzes?.length > 0
    ? Math.round(selectedChild.recent_quizzes.reduce((a, b) => a + b.percentage, 0) / selectedChild.recent_quizzes.length)
    : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="parent-dashboard">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-md sticky top-0 z-40 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FD5E53] to-[#F97316] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-fredoka text-xl font-semibold tracking-tight text-slate-800">Craven-it</span>
          </div>

          <nav className="hidden md:flex items-center gap-1.5 bg-slate-50 rounded-full p-1.5">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-full font-fredoka font-medium text-sm transition-all flex items-center gap-2 ${
                  activeTab === t.id
                    ? 'bg-[#38BDF8] text-white shadow-md'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                data-testid={`parent-nav-${t.id}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <NotificationsPopover
              onOpenRefresh={fetchDashboard}
            />

            <div className="hidden sm:flex items-center gap-2.5 pl-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[#FDE047] to-[#F59E0B] rounded-full flex items-center justify-center font-fredoka font-bold text-white">
                {user?.name?.[0]?.toUpperCase() || 'P'}
              </div>
              <span className="font-nunito font-semibold text-slate-700 hidden lg:block">{user?.name}</span>
            </div>

            <button
              onClick={handleLogout}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 rounded-full font-fredoka font-medium text-sm whitespace-nowrap flex items-center gap-1.5 transition-all ${
                activeTab === t.id ? 'bg-[#38BDF8] text-white shadow-md' : 'bg-white text-slate-500 border border-slate-100'
              }`}
              data-testid={`parent-mobile-nav-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Children tabs */}
        {dashboardData?.children?.length > 0 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {dashboardData.children.map((child) => {
              const isActive = selectedChild?.id === child.id;
              return (
                <button
                  key={child.id}
                  onClick={() => setSelectedChild(child)}
                  className={`flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full font-fredoka font-medium whitespace-nowrap transition-all text-sm ${
                    isActive
                      ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/20'
                      : 'bg-white text-slate-500 border border-slate-100 hover:border-slate-200'
                  }`}
                  data-testid={`child-tab-${child.id}`}
                >
                  {child.avatar_url ? (
                    <img
                      src={child.avatar_url}
                      alt={child.name}
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-fredoka font-semibold text-sm flex-shrink-0 ${
                      isActive ? 'bg-white text-slate-800' : 'bg-gradient-to-br from-[#FDE047] to-[#F59E0B] text-white'
                    }`}>
                      {child.name?.[0]?.toUpperCase() || 'K'}
                    </div>
                  )}
                  <span>{child.name} <span className="opacity-60">· Grade {child.grade}</span></span>
                </button>
              );
            })}
            <button
              onClick={() => setChildDialog({ open: true, child: null })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-fredoka font-medium whitespace-nowrap text-sm bg-white text-[#FD5E53] border-2 border-dashed border-[#FD5E53]/40 hover:border-[#FD5E53] hover:bg-[#FFF5F4] transition-all"
              data-testid="add-child-tab-btn"
            >
              <Plus className="w-4 h-4" /> Add child
            </button>
            {selectedChild && (
              <>
                <button
                  onClick={() => setChildDialog({ open: true, child: selectedChild })}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-white border border-slate-100 text-slate-500 hover:text-[#38BDF8] hover:border-[#38BDF8] transition-all"
                  title={`Edit ${selectedChild.name}`}
                  aria-label={`Edit ${selectedChild.name}`}
                  data-testid="edit-child-btn"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeleteTarget(selectedChild)}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-white border border-slate-100 text-slate-500 hover:text-red-500 hover:border-red-300 transition-all"
                  title={`Remove ${selectedChild.name}`}
                  aria-label={`Remove ${selectedChild.name}`}
                  data-testid="delete-child-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        )}

        {activeTab === 'family' ? (
          <FamilyTab
            children={dashboardData?.children || []}
            subscriptions={subscriptions}
            user={user}
            onSelect={(id) => {
              const c = dashboardData?.children?.find((ch) => ch.id === id);
              if (c) { setSelectedChild(c); setActiveTab('overview'); }
            }}
          />
        ) : selectedChild ? (
          <>
            {activeTab === 'content' && (
              <ContentViewer
                child={selectedChild}
                isSubscribed={!!subscriptions.find((s) => s.child_id === selectedChild.id)?.is_active}
                onSubscribeClick={() => setActiveTab('overview')}
              />
            )}
            {activeTab === 'quizzes' && (
              <QuizTaker
                child={selectedChild}
                isSubscribed={!!subscriptions.find((s) => s.child_id === selectedChild.id)?.is_active}
                onSubscribeClick={() => setActiveTab('overview')}
              />
            )}
            {activeTab === 'badges' && <BadgeGallery child={selectedChild} />}

            {activeTab === 'overview' && (
              <OverviewTab
                user={user}
                child={selectedChild}
                dashboardData={dashboardData}
                avgScore={avgScore}
                downloadReport={downloadReport}
                subscription={subscriptions.find((s) => s.child_id === selectedChild.id)}
                onChildUpdated={(updated) => {
                  setDashboardData((d) => ({
                    ...d,
                    children: d.children.map((c) => c.id === updated.id ? { ...c, ...updated } : c)
                  }));
                  setSelectedChild((prev) => prev?.id === updated.id ? { ...prev, ...updated } : prev);
                }}
              />
            )}
          </>
        ) : (
          <div className={`${CARD_BASE} text-center py-16`}>
            <Users className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h2 className="font-fredoka text-2xl font-semibold text-slate-800 mb-2">No children added yet</h2>
            <p className="font-nunito text-slate-500 max-w-md mx-auto mb-6">
              Add your little learner to unlock lessons, quizzes, badges, and more.
            </p>
            <button
              onClick={() => setChildDialog({ open: true, child: null })}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-semibold text-sm shadow-md transition-all"
              data-testid="add-child-empty-btn"
            >
              <Plus className="w-4 h-4" /> Add your first child
            </button>
          </div>
        )}
      </main>
      <ParentChatBubble />
      <ChildFormDialog
        open={childDialog.open}
        onOpenChange={(v) => setChildDialog((p) => ({ ...p, open: v }))}
        parentId={user?.id}
        child={childDialog.child}
        onSaved={(savedChild, action) => {
          if (action === 'create') {
            setDashboardData((d) => ({
              ...(d || { children: [] }),
              children: [...((d?.children) || []), savedChild],
            }));
            setSelectedChild(savedChild);
          } else {
            // update
            setDashboardData((d) => ({
              ...d,
              children: (d?.children || []).map((c) => c.id === savedChild.id ? { ...c, ...savedChild } : c),
            }));
            setSelectedChild((prev) => prev?.id === savedChild.id ? { ...prev, ...savedChild } : prev);
          }
          fetchDashboard();
        }}
      />
      <DeleteChildConfirm
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        child={deleteTarget}
        onDeleted={(deletedId) => {
          setDashboardData((d) => {
            const next = (d?.children || []).filter((c) => c.id !== deletedId);
            return { ...d, children: next };
          });
          setSelectedChild((prev) => {
            if (prev?.id !== deletedId) return prev;
            const remaining = (dashboardData?.children || []).filter((c) => c.id !== deletedId);
            return remaining[0] || null;
          });
          setDeleteTarget(null);
          fetchDashboard();
        }}
      />
    </div>
  );
}

// ------------ OVERVIEW TAB ------------
function OverviewTab({ user, child, dashboardData, avgScore, downloadReport, onChildUpdated, subscription }) {
  const stats = [
    { label: 'Streak', value: `${child.streak_days ?? 0}🔥`, icon: Sparkles, bg: 'bg-[#FFE4E6]', fg: 'text-[#FD5E53]' },
    { label: 'Points', value: child.points ?? 0, icon: Star, bg: 'bg-[#FEF9C3]', fg: 'text-[#D97706]' },
    { label: 'Badges', value: child.badges?.length || 0, icon: Trophy, bg: 'bg-[#FFEDD5]', fg: 'text-[#FD5E53]' },
    { label: 'Quizzes', value: child.recent_quizzes?.length || 0, icon: Award, bg: 'bg-[#DCFCE7]', fg: 'text-[#16A34A]' },
    { label: 'Avg Score', value: avgScore !== null ? `${avgScore}%` : '—', icon: TrendingUp, bg: 'bg-[#E0F2FE]', fg: 'text-[#0284C7]' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
      {/* MAIN COLUMN */}
      <div className="lg:col-span-8 space-y-6 lg:space-y-8">
        {/* HERO WELCOME */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] p-6 sm:p-8 border border-white/60"
        >
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="flex-1 min-w-0">
              <p className="font-nunito text-xs sm:text-sm font-bold uppercase tracking-wider text-[#FD5E53] mb-2">
                Welcome back
              </p>
              <h1 className="font-fredoka text-2xl sm:text-4xl font-semibold text-slate-800 tracking-tight leading-tight">
                Hi {user?.name?.split(' ')[0] || 'there'}!
              </h1>
              <p className="font-nunito text-slate-600 mt-2 text-sm sm:text-base">
                <span className="font-semibold text-slate-800">{child.name}</span> has earned{' '}
                <span className="font-fredoka font-semibold text-[#FD5E53]">{child.points ?? 0} points</span>
                {child.badges?.length > 0 && (
                  <> and <span className="font-fredoka font-semibold text-[#D97706]">{child.badges.length} badges</span></>
                )}
                {child.streak_days > 0 && (
                  <> · <span className="font-fredoka font-semibold text-[#FD5E53]">{child.streak_days}-day streak 🔥</span></>
                )}
                . Keep shining! ✨
              </p>
            </div>
            <ChildAvatar child={child} onUpdated={onChildUpdated} />
          </div>
        </motion.div>

        {/* STATS GRID */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className={`${CARD_BASE} !p-5 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all`}
            >
              <div className={`w-11 h-11 rounded-2xl ${s.bg} flex items-center justify-center mb-3`}>
                <s.icon className={`w-5 h-5 ${s.fg}`} />
              </div>
              <p className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 leading-none">{s.value}</p>
              <p className="font-nunito text-xs text-slate-500 uppercase tracking-wide font-bold mt-1.5">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* SUBJECT PROGRESS */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className={CARD_BASE}
        >
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
            <div>
              <h2 className="font-fredoka text-xl sm:text-2xl font-semibold text-slate-800">Subject progress</h2>
              <p className="font-nunito text-sm text-slate-500">How {child.name} is doing across subjects</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => downloadReport(child.id, 'pdf')}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-fredoka font-medium text-[#0284C7] bg-[#E0F2FE] hover:bg-[#BAE6FD] transition-colors"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4" /> PDF
              </button>
              <button
                onClick={() => downloadReport(child.id, 'excel')}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-fredoka font-medium text-[#16A34A] bg-[#DCFCE7] hover:bg-[#BBF7D0] transition-colors"
                data-testid="download-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {child.progress?.length > 0 ? (
              child.progress.map((subject, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-1.5 items-baseline">
                    <span className="font-nunito font-semibold text-slate-700 text-sm sm:text-base">
                      {subject.subject}
                    </span>
                    <span className="font-fredoka font-semibold text-[#16A34A] text-sm">
                      {Math.round(subject.completion)}%
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-[#86EFAC] to-[#22C55E]"
                      initial={{ width: 0 }}
                      animate={{ width: `${subject.completion}%` }}
                      transition={{ duration: 1, delay: 0.3 + i * 0.08 }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="font-nunito text-slate-400 text-sm">No subjects assigned yet.</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* RECENT QUIZZES */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className={CARD_BASE}
        >
          <h2 className="font-fredoka text-xl sm:text-2xl font-semibold text-slate-800 mb-4">Recent quizzes</h2>
          {child.recent_quizzes?.length > 0 ? (
            <div className="space-y-2.5">
              {child.recent_quizzes.map((quiz, i) => {
                const pct = Math.round(quiz.percentage);
                const color = pct >= 80 ? { bg: 'bg-[#DCFCE7]', fg: 'text-[#16A34A]' }
                  : pct >= 60 ? { bg: 'bg-[#FEF9C3]', fg: 'text-[#CA8A04]' }
                  : { bg: 'bg-[#FFE4E6]', fg: 'text-[#FD5E53]' };
                return (
                  <div key={i} className="flex items-center justify-between p-3.5 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${color.bg} flex items-center justify-center`}>
                        <Award className={`w-5 h-5 ${color.fg}`} />
                      </div>
                      <div>
                        <p className="font-nunito font-semibold text-slate-800 text-sm">Quiz #{i + 1}</p>
                        <p className="font-nunito text-xs text-slate-500">{quiz.score} / {quiz.total} points</p>
                      </div>
                    </div>
                    <span className={`font-fredoka text-xl font-semibold ${color.fg}`}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Award className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="font-nunito text-slate-400 text-sm">No quizzes taken yet.</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* SIDEBAR */}
      <aside className="lg:col-span-4 space-y-6 lg:space-y-8">
        {/* SUBSCRIPTION */}
        <SubscriptionCard child={child} subscription={subscription} />

        {/* UPCOMING CLASSES */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={CARD_BASE}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-fredoka text-xl font-semibold text-slate-800">Upcoming</h2>
            <Calendar className="w-5 h-5 text-slate-300" />
          </div>

          {dashboardData?.upcoming_classes?.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.upcoming_classes.slice(0, 3).map((cls) => (
                <div key={cls.id} className="p-4 rounded-2xl bg-gradient-to-br from-[#E0F2FE] to-[#F0F9FF] border border-[#BAE6FD]/40">
                  <p className="font-fredoka font-medium text-slate-800 text-base">{cls.title}</p>
                  <p className="font-nunito text-xs text-slate-500 mb-2.5">{cls.subject}</p>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-3">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="font-nunito">
                      {new Date(cls.scheduled_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      {' · '}
                      {new Date(cls.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {subscription?.is_active ? (
                    <a
                      href={cls.google_meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#38BDF8] hover:bg-[#0EA5E9] text-white text-xs font-fredoka font-medium px-3 py-1.5 rounded-full transition-colors"
                      data-testid={`join-class-${cls.id}`}
                    >
                      <Video className="w-3.5 h-3.5" /> Join class
                    </a>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 bg-slate-200 text-slate-500 text-xs font-fredoka font-medium px-3 py-1.5 rounded-full cursor-not-allowed"
                      title="Subscribe to join live classes"
                      data-testid={`join-class-locked-${cls.id}`}
                    >
                      <Lock className="w-3.5 h-3.5" /> Subscribe to join
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Calendar className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="font-nunito text-slate-400 text-sm">No upcoming classes.</p>
            </div>
          )}
        </motion.div>

        {/* SEESAW LINK */}
        <motion.a
          href="https://app.seesaw.me/"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="flex items-center justify-between gap-3 rounded-[2rem] p-5 bg-gradient-to-r from-[#E0F2FE] to-[#DCFCE7] border border-[#BAE6FD]/40 hover:-translate-y-0.5 transition-transform shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          data-testid="seesaw-link"
        >
          <div>
            <p className="font-fredoka font-semibold text-slate-800 text-base">Open Seesaw</p>
            <p className="font-nunito text-xs text-slate-500">Continue learning in the Seesaw app</p>
          </div>
          <span className="inline-flex items-center gap-1.5 bg-[#38BDF8] text-white text-xs font-fredoka font-medium px-4 py-2 rounded-full flex-shrink-0">
            Go <Rocket className="w-3.5 h-3.5" />
          </span>
        </motion.a>

        {/* NOTIFICATIONS */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={CARD_BASE}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-fredoka text-xl font-semibold text-slate-800">Updates</h2>
            <Bell className="w-5 h-5 text-slate-300" />
          </div>

          {dashboardData?.recent_notifications?.length > 0 ? (
            <div className="space-y-2.5">
              {dashboardData.recent_notifications.slice(0, 4).map((n) => (
                <div key={n.id} className="p-3 rounded-2xl bg-[#FEF9C3]/60 border border-[#FDE68A]/40">
                  <p className="font-fredoka font-medium text-slate-800 text-sm">{n.title}</p>
                  <p className="font-nunito text-xs text-slate-600 mt-1 leading-relaxed">{n.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="font-nunito text-slate-400 text-sm">No new updates.</p>
            </div>
          )}
        </motion.div>

        {/* BADGES MINI */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`${CARD_BASE} relative overflow-hidden`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-fredoka text-xl font-semibold text-slate-800">Badges</h2>
            <Trophy className="w-5 h-5 text-slate-300" />
          </div>

          {child.badges?.length > 0 ? (
            <div className="flex flex-wrap gap-2.5">
              {child.badges.map((badge, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.08, type: 'spring' }}
                  className="w-12 h-12 bg-gradient-to-br from-[#FDE047] to-[#F59E0B] rounded-full flex items-center justify-center shadow-md"
                  title={badge.replace(/_/g, ' ')}
                >
                  <Trophy className="w-6 h-6 text-white" />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-slate-50 mb-2">
                <Rocket className="w-6 h-6 text-slate-300" />
              </div>
              <p className="font-nunito text-slate-400 text-sm">Complete lessons to earn your first badge!</p>
            </div>
          )}
        </motion.div>
      </aside>
    </div>
  );
}

// ------------ CHILD AVATAR (Upload + Display) ------------
function ChildAvatar({ child, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const fileRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploading(true);
    try {
      // Upload
      const fd = new FormData();
      fd.append('file', file);
      const up = await fetch(`${BACKEND}/api/uploads`, {
        method: 'POST',
        credentials: 'include',
        headers: (() => { const t = localStorage.getItem('craven_access_token'); return t ? { Authorization: `Bearer ${t}` } : {}; })(),
        body: fd,
      });
      if (!up.ok) throw new Error('Upload failed');
      const upData = await up.json();
      const avatar_url = `${BACKEND}${upData.url}`;
      // Persist on child
      const { data } = await api.put(`/api/children/${child.id}`, { avatar_url });
      onUpdated?.({ id: child.id, avatar_url: data.avatar_url });
      toast.success('Photo updated!');
      setDialogOpen(false);
    } catch {
      toast.error('Could not update photo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const savePreset = async () => {
    if (!selectedPreset) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/api/children/${child.id}`, { avatar_url: selectedPreset });
      onUpdated?.({ id: child.id, avatar_url: data.avatar_url });
      toast.success('Avatar updated!');
      setDialogOpen(false);
      setSelectedPreset(null);
    } catch {
      toast.error('Could not update avatar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative flex-shrink-0" data-testid="child-avatar">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid="child-avatar-input"
      />
      <button
        type="button"
        onClick={() => { setSelectedPreset(child.avatar_url || null); setDialogOpen(true); }}
        disabled={uploading}
        className="group relative w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden shadow-xl border-4 border-white hover:scale-105 transition-transform disabled:opacity-60"
        data-testid="child-avatar-btn"
      >
        {child.avatar_url ? (
          <img src={child.avatar_url} alt={child.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FDE047] via-[#F59E0B] to-[#FD5E53] flex items-center justify-center font-fredoka text-4xl sm:text-5xl font-semibold text-white">
            {child.name?.[0]?.toUpperCase() || 'K'}
          </div>
        )}
        {/* Camera overlay */}
        <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          {uploading ? (
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
            />
          ) : (
            <Camera className="w-7 h-7 text-white" />
          )}
        </div>
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full rounded-3xl overflow-hidden" data-testid="avatar-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl text-slate-800">
              Choose {child.name}'s avatar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <AvatarPicker value={selectedPreset} onChange={setSelectedPreset} />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || saving}
                className="flex-1 px-4 py-2.5 rounded-full border-2 border-slate-100 text-slate-600 font-fredoka font-medium text-sm hover:bg-slate-50 flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="avatar-upload-photo-btn"
              >
                <Camera className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload photo'}
              </button>
              <button
                type="button"
                onClick={savePreset}
                disabled={!selectedPreset || saving || selectedPreset === child.avatar_url}
                className="flex-1 px-4 py-2.5 rounded-full bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="avatar-save-preset-btn"
              >
                {saving ? 'Saving…' : 'Use this avatar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ------------ SUBSCRIPTION CARD ------------
function SubscriptionCard({ child, subscription }) {
  const [creating, setCreating] = useState(false);
  const [pkg, setPkg] = useState(null);
  const isActive = subscription?.is_active;
  const expiresAt = subscription?.expires_at ? new Date(subscription.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/subscriptions/packages');
        if (!cancelled && Array.isArray(data) && data.length) setPkg(data[0]);
      } catch (_) { /* silent — fallback UI still renders */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const startCheckout = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/api/subscriptions/checkout', {
        package_id: 'monthly_2sessions',
        child_id: child.id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout');
        setCreating(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-[2rem] p-6 border ${
        isActive
          ? 'bg-gradient-to-br from-[#DCFCE7] via-[#F0FDF4] to-[#ECFDF5] border-[#86EFAC]/40'
          : 'bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] border-[#FDE68A]/40'
      }`}
      data-testid="subscription-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-fredoka text-xl font-semibold text-slate-800">
          {isActive ? 'Subscription' : 'Subscribe'}
        </h2>
        <CreditCard className="w-5 h-5 text-slate-400" />
      </div>

      {isActive ? (
        <>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-[#16A34A]" />
            <span className="font-fredoka font-medium text-[#16A34A]">Active</span>
          </div>
          <p className="font-nunito text-sm text-slate-700 mb-1">
            {child.name} · Monthly plan
          </p>
          <p className="font-nunito text-xs text-slate-500">
            Renews in <span className="font-fredoka font-semibold text-slate-700">{daysLeft} days</span>
            {expiresAt && ` (${expiresAt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })})`}
          </p>
          <button
            onClick={startCheckout}
            disabled={creating}
            className="mt-4 w-full bg-white/80 hover:bg-white text-slate-700 text-sm font-fredoka font-medium py-2 rounded-full transition-all disabled:opacity-60 border border-[#86EFAC]/40"
            data-testid="renew-subscription-btn"
          >
            {creating ? 'Redirecting…' : 'Renew for another month'}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-1.5 mb-1">
            <span className="font-fredoka text-3xl font-semibold text-slate-800" data-testid="subscription-price">
              ${pkg ? (Number.isInteger(pkg.amount) ? pkg.amount : pkg.amount.toFixed(2)) : '200'}
            </span>
            <span className="font-nunito text-sm text-slate-500">/ month</span>
          </div>
          <p className="font-nunito text-sm text-slate-600 mb-3">
            {pkg?.description ? (
              pkg.description
            ) : (
              <>2 tutoring sessions per week for <span className="font-semibold">{child.name}</span></>
            )}
          </p>
          <div className="space-y-1.5 mb-5">
            <BenefitLine>Personalized K–2 lessons</BenefitLine>
            <BenefitLine>Live classes with Tracey</BenefitLine>
            <BenefitLine>Progress reports & badges</BenefitLine>
          </div>
          <button
            onClick={startCheckout}
            disabled={creating}
            className="w-full bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium py-3 rounded-full shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="subscribe-btn"
          >
            {creating ? (
              <>
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block"
                />
                Redirecting…
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" /> Subscribe now
              </>
            )}
          </button>
        </>
      )}
    </motion.div>
  );
}

function BenefitLine({ children }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-700 font-nunito">
      <CheckCircle2 className="w-4 h-4 text-[#16A34A] flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}


// ------------ FAMILY TAB (multi-child summary) ------------
function FamilyTab({ children, subscriptions, user, onSelect }) {
  if (!children || children.length === 0) {
    return (
      <div className="bg-white rounded-[2rem] p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50" data-testid="family-tab">
        <Users className="w-14 h-14 text-slate-200 mx-auto mb-3" />
        <p className="font-nunito text-slate-500">No children added yet.</p>
      </div>
    );
  }

  const totalPoints = children.reduce((a, c) => a + (c.points || 0), 0);
  const totalBadges = children.reduce((a, c) => a + (c.badges?.length || 0), 0);
  const bestStreak = children.reduce((a, c) => Math.max(a, c.streak_days || 0), 0);

  return (
    <div data-testid="family-tab">
      {/* Family stats header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] p-6 sm:p-8 border border-white/60 mb-6"
      >
        <p className="font-nunito text-xs font-bold uppercase tracking-wider text-[#FD5E53] mb-2">Family snapshot</p>
        <h1 className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
          The {user?.name?.split(' ').slice(-1)[0] || 'Craven'} crew
        </h1>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <SummaryStat label="Children" value={children.length} />
          <SummaryStat label="Total Points" value={totalPoints} />
          <SummaryStat label="Total Badges" value={totalBadges} />
        </div>
        {bestStreak > 0 && (
          <p className="mt-3 font-nunito text-sm text-slate-700">
            🔥 Best streak in the family: <span className="font-fredoka font-semibold">{bestStreak} day{bestStreak !== 1 ? 's' : ''}</span>
          </p>
        )}
      </motion.div>

      {/* Child cards */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
        {children.map((child, i) => {
          const sub = subscriptions.find((s) => s.child_id === child.id);
          const isActive = sub?.is_active;
          const avgPct = child.recent_quizzes?.length > 0
            ? Math.round(child.recent_quizzes.reduce((a, b) => a + b.percentage, 0) / child.recent_quizzes.length)
            : null;
          return (
            <motion.button
              key={child.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -3 }}
              onClick={() => onSelect(child.id)}
              className="text-left bg-white rounded-[1.75rem] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all"
              data-testid={`family-child-${child.id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                {child.avatar_url ? (
                  <img src={child.avatar_url} alt={child.name} className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FDE047] to-[#F59E0B] flex items-center justify-center font-fredoka text-xl font-semibold text-white">
                    {child.name?.[0]?.toUpperCase() || 'K'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-fredoka text-lg font-semibold text-slate-800 truncate">{child.name}</p>
                  <p className="font-nunito text-xs text-slate-500">Grade {child.grade}</p>
                </div>
                <span className={`text-[10px] font-fredoka font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                  isActive ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#FEF9C3] text-[#CA8A04]'
                }`}>
                  {isActive ? 'Active' : 'No plan'}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2 mb-3">
                <MiniStat label="Streak" value={`${child.streak_days ?? 0}🔥`} />
                <MiniStat label="Points" value={child.points ?? 0} />
                <MiniStat label="Badges" value={child.badges?.length || 0} />
                <MiniStat label="Avg" value={avgPct !== null ? `${avgPct}%` : '—'} />
              </div>

              <div className="mt-2 text-xs text-[#FD5E53] font-fredoka font-medium">
                View details →
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-3 text-center">
      <p className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 leading-none">{value}</p>
      <p className="font-nunito text-[11px] text-slate-500 uppercase tracking-wide font-bold mt-1">{label}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl p-2 text-center">
      <p className="font-fredoka text-sm font-semibold text-slate-800 leading-none">{value}</p>
      <p className="font-nunito text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">{label}</p>
    </div>
  );
}


// ------------ CHILD FORM DIALOG (add + edit) ------------
function ChildFormDialog({ open, onOpenChange, parentId, child, onSaved }) {
  const editing = !!child?.id;
  const [name, setName] = useState('');
  const [grade, setGrade] = useState('');
  const [dob, setDob] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // When opening in edit mode, prefill from child
  useEffect(() => {
    if (open && editing) {
      setName(child.name || '');
      setGrade(child.grade || '');
      setDob(child.date_of_birth || '');
      setAvatarUrl(child.avatar_url || null);
      setError('');
    }
    if (open && !editing) {
      setName(''); setGrade(''); setDob(''); setAvatarUrl(null); setError('');
    }
  }, [open, editing, child]);

  const handleClose = (next) => {
    if (!next) { setError(''); }
    onOpenChange(next);
  };

  const submit = async () => {
    setError('');
    if (!name.trim() || !grade) {
      setError("Please enter your child's name and grade.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/api/children/${child.id}`, {
          name: name.trim(),
          grade,
          date_of_birth: dob || null,
          avatar_url: avatarUrl || null,
        });
        toast.success(`${data.name} updated`);
        onSaved?.(data, 'update');
      } else {
        const { data } = await api.post('/api/children', {
          name: name.trim(),
          grade,
          date_of_birth: dob || null,
          avatar_url: avatarUrl || null,
          parent_id: parentId,
        });
        toast.success(`${data.name} added! 🎉`);
        onSaved?.(data, 'create');
      }
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || `Could not ${editing ? 'update' : 'add'} child. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full rounded-3xl overflow-hidden" data-testid={editing ? 'edit-child-dialog' : 'add-child-dialog'}>
        <DialogHeader>
          <DialogTitle className="font-fredoka text-xl text-slate-800 flex items-center gap-2">
            {editing ? <Pencil className="w-5 h-5 text-[#38BDF8]" /> : <Sparkles className="w-5 h-5 text-[#FD5E53]" />}
            {editing ? `Edit ${child.name}` : 'Add a child'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1 w-full min-w-0">
          <div className="space-y-1.5">
            <label htmlFor="child-form-name" className="block text-xs font-nunito font-semibold text-slate-600">
              Child's name
            </label>
            <input
              id="child-form-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Emma"
              className="w-full rounded-xl border-2 border-slate-100 focus:border-[#38BDF8] focus:outline-none py-2.5 px-3 font-nunito text-sm bg-white"
              data-testid="add-child-name"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="child-form-grade" className="block text-xs font-nunito font-semibold text-slate-600">
              Grade
            </label>
            <select
              id="child-form-grade"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="w-full rounded-xl border-2 border-slate-100 focus:border-[#38BDF8] focus:outline-none py-2.5 px-3 font-nunito text-sm bg-white"
              data-testid="add-child-grade"
            >
              <option value="">Select grade</option>
              <option value="K">Kindergarten</option>
              <option value="1">1st Grade</option>
              <option value="2">2nd Grade</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="child-form-dob" className="block text-xs font-nunito font-semibold text-slate-600">
              Date of Birth <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              id="child-form-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full rounded-xl border-2 border-slate-100 focus:border-[#38BDF8] focus:outline-none py-2.5 px-3 font-nunito text-sm bg-white"
              data-testid="add-child-dob"
            />
          </div>
          <AvatarPicker value={avatarUrl} onChange={setAvatarUrl} />

          {error && (
            <div className="bg-red-50 text-red-600 border border-red-100 rounded-xl px-3 py-2 text-sm font-nunito" data-testid="add-child-error">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => handleClose(false)}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-full border-2 border-slate-100 text-slate-600 font-fredoka font-medium text-sm hover:bg-slate-50 disabled:opacity-50"
              data-testid="add-child-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || !name.trim() || !grade}
              className="flex-1 px-4 py-2.5 rounded-full bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              data-testid="add-child-submit"
            >
              {saving ? (
                <>
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block"
                  />
                  {editing ? 'Saving…' : 'Adding…'}
                </>
              ) : (
                <>{editing ? 'Save changes' : 'Add child'} <CheckCircle2 className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------ DELETE CHILD CONFIRM ------------
function DeleteChildConfirm({ open, onOpenChange, child, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const confirm = async () => {
    if (!child?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/api/children/${child.id}`);
      toast.success(`${child.name} removed`);
      onDeleted?.(child.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete child');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm w-[calc(100vw-2rem)] sm:w-full rounded-3xl" data-testid="delete-child-dialog">
        <DialogHeader>
          <DialogTitle className="font-fredoka text-xl text-slate-800 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" /> Remove {child?.name}?
          </DialogTitle>
        </DialogHeader>
        <p className="font-nunito text-sm text-slate-600">
          This will permanently delete {child?.name}'s profile and all their progress, quiz results, and attendance history. This cannot be undone.
        </p>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 rounded-full border-2 border-slate-100 text-slate-600 font-fredoka font-medium text-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="delete-child-cancel"
          >
            Keep
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white font-fredoka font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="delete-child-confirm"
          >
            {deleting ? (
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block"
              />
            ) : (
              <><Trash2 className="w-4 h-4" /> Delete</>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ------------ NOTIFICATIONS POPOVER ------------
function NotificationsPopover({ onOpenRefresh }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/notifications');
      setItems(data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const unreadCount = items.filter((n) => !n.read).length;

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) load();
  };

  const markOne = async (id) => {
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    try { await api.put(`/api/notifications/${id}/read`); } catch { /* ignore */ }
  };

  const markAll = async () => {
    if (unreadCount === 0) return;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.put('/api/notifications/read-all');
      toast.success('All notifications marked as read');
      onOpenRefresh?.();
    } catch { toast.error('Could not mark all as read'); }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 hover:bg-slate-100 rounded-full transition-colors"
          data-testid="notifications-btn"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[1rem] h-4 px-1 bg-[#FD5E53] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              data-testid="notifications-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 sm:w-96 p-0 rounded-2xl border-slate-100 shadow-xl overflow-hidden"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-[#FFF5F4] to-white">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#FD5E53]" />
            <span className="font-fredoka font-semibold text-slate-800">Notifications</span>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAll}
              className="text-xs font-nunito font-semibold text-[#FD5E53] hover:text-[#E84E44]"
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="py-8 flex justify-center">
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-5 h-5 border-2 border-[#FD5E53] border-t-transparent rounded-full"
              />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center" data-testid="notifications-empty">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="font-nunito text-sm text-slate-400">You're all caught up!</p>
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => markOne(n.id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition-colors flex gap-3 ${
                  !n.read ? 'bg-[#FFF8F7]' : ''
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-[#FD5E53]' : 'bg-slate-200'}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-fredoka font-medium text-sm text-slate-800 leading-snug">{n.title}</p>
                  {n.message && (
                    <p className="font-nunito text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                  )}
                  <p className="font-nunito text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
